import { useState, useCallback, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/articles';

export default function Footer() {
  const [status, setStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');

  const handleSubmit = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('company_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setStatus('success');
      setStatusMsg('订阅成功！感谢您的关注。');
      form.reset();
      return;
    }

    formData.delete('company_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d96b9u70d76aer3t52bg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as URLSearchParams).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        // not JSON
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        setStatusMsg('订阅成功！感谢您的关注。');
        form.reset();
      } else {
        setStatus('error');
        setStatusMsg(serverMsg || '订阅失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setStatusMsg('网络错误，请检查网络连接后重试。');
    }
  }, []);

  return (
    <footer className="relative bg-foreground-950 text-background-50 rounded-t-[32px] mt-16 md:mt-24">
      <div className="px-4 md:px-6 py-16 md:py-20 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-12">
          <div className="lg:col-span-1">
            <h3 className="text-2xl font-heading font-bold mb-6">随时随地的吃瓜自由</h3>
            <p className="text-foreground-400 text-sm leading-relaxed">
              五一吃瓜网是您的吃瓜第一站，支持在线观看各类网红明星黑料爆料。独家内容每日更新，吃瓜从未如此直接。
            </p>
          </div>

          <div>
            <h4 className="text-base font-semibold mb-5">快速链接</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/" className="text-foreground-400 hover:text-background-50 transition-colors text-sm whitespace-nowrap">
                  首页
                </Link>
              </li>
              {categories.map((cat) => (
                <li key={cat.slug}>
                  <Link to={`/category/${cat.slug}`} className="text-foreground-400 hover:text-background-50 transition-colors text-sm whitespace-nowrap">
                    {cat.name}
                  </Link>
                </li>
              ))}
              <li>
                <Link to="/about" className="text-foreground-400 hover:text-background-50 transition-colors text-sm whitespace-nowrap">
                  关于我们
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-base font-semibold mb-5">关注我们</h4>
            <ul className="space-y-3">
              <li>
                <a href="#" className="flex items-center gap-3 text-foreground-400 hover:text-background-50 transition-colors text-sm" rel="nofollow">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-weibo-line"></i>
                  </span>
                  微博
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-3 text-foreground-400 hover:text-background-50 transition-colors text-sm" rel="nofollow">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-wechat-line"></i>
                  </span>
                  微信公众号
                </a>
              </li>
              <li>
                <a href="#" className="flex items-center gap-3 text-foreground-400 hover:text-background-50 transition-colors text-sm" rel="nofollow">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-tiktok-line"></i>
                  </span>
                  抖音
                </a>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="text-base font-semibold mb-5">订阅爆料推送</h4>
            <p className="text-foreground-400 text-sm mb-4 leading-relaxed">
              第一时间获取最新爆料，不错过任何一个瓜。
            </p>
            <form
              className="flex gap-2"
              data-readdy-form="newsletter"
              id="footer-newsletter"
              method="POST"
              onSubmit={handleSubmit}
            >
              <input
                type="email"
                name="email"
                placeholder="输入您的邮箱"
                required
                className="flex-1 px-4 py-2.5 rounded-lg bg-foreground-800/50 border border-foreground-700/30 text-sm text-background-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-500/50 transition-colors"
              />
              <input
                type="text"
                name="company_alt"
                className="anti-spam-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <button
                type="submit"
                className="w-10 h-10 rounded-full bg-primary-500 hover:bg-primary-600 flex items-center justify-center transition-colors cursor-pointer whitespace-nowrap flex-shrink-0"
                aria-label="订阅"
              >
                <i className="ri-arrow-right-line text-background-50"></i>
              </button>
            </form>
            {status !== 'idle' && (
              <p className={`mt-3 text-xs ${status === 'success' ? 'text-accent-300' : 'text-red-400'}`}>
                {statusMsg}
              </p>
            )}
          </div>
        </div>

        <div className="mt-14 pt-8 border-t border-foreground-800/40 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-foreground-500 text-xs">
            &copy; {new Date().getFullYear()} 五一吃瓜网 版权所有
          </p>
          <div className="flex items-center gap-6">
            <a href="#" className="text-foreground-500 hover:text-background-50 transition-colors text-xs" rel="nofollow">隐私政策</a>
            <a href="#" className="text-foreground-500 hover:text-background-50 transition-colors text-xs" rel="nofollow">用户协议</a>
            <a href="#" className="text-foreground-500 hover:text-background-50 transition-colors text-xs" rel="nofollow">免责声明</a>
          </div>
        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 overflow-hidden pointer-events-none select-none">
        <span className="block text-[120px] md:text-[160px] font-heading font-black text-foreground-900/10 leading-none whitespace-nowrap text-center">
          五一吃瓜网
        </span>
      </div>
    </footer>
  );
}